'use client';

import { Inbox, MessageSquarePlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { SheetTitle } from '@/components/ui/sheet';
import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { cn } from '@/lib/utils';

import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import type { PostHogSupportView } from './posthog-support-state';

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;

export function PostHogSupportHeader({
  actionsAvailable = true,
  compact,
  copy,
  dialog,
  isRefreshingRequests,
  onNewRequest,
  onOpenRequests,
  view,
}: {
  actionsAvailable?: boolean;
  compact: boolean;
  copy: SupportCopy;
  dialog: boolean;
  isRefreshingRequests: boolean;
  onNewRequest: () => void;
  onOpenRequests: () => void;
  view: PostHogSupportView;
}) {
  const isNewRequestView = view === 'choose' || view === 'compose';
  const isRequestsView = view === 'requests';

  return (
    <header
      className={cn(
        'border-border/35 shrink-0 border-b',
        getPostHogSupportHorizontalPadding(compact),
        compact ? 'py-3 pe-14' : 'py-5 sm:py-6'
      )}
    >
      <div
        className={cn(
          'flex items-start justify-between gap-3',
          getPostHogSupportContentWidth(compact),
          !compact && 'flex-col gap-4 sm:flex-row sm:items-center'
        )}
      >
        <div className="min-w-0">
          {compact && dialog ? (
            <SheetTitle asChild>
              <h2 className="text-start text-base font-semibold text-balance">
                {copy.title}
              </h2>
            </SheetTitle>
          ) : compact ? (
            <h2 className="text-start text-base font-semibold text-balance">
              {copy.title}
            </h2>
          ) : (
            <h1 className="text-xl font-semibold text-balance sm:text-2xl">
              {copy.title}
            </h1>
          )}
          <p
            className={cn(
              'text-muted-foreground mt-1 max-w-2xl leading-5 text-pretty',
              compact ? 'line-clamp-2 text-xs' : 'text-sm'
            )}
          >
            {copy.description}
          </p>
        </div>
        {actionsAvailable ? (
          <div
            className={cn(
              'shrink-0 items-center gap-1',
              compact ? 'flex' : 'grid w-full grid-cols-2 sm:flex sm:w-auto'
            )}
          >
            <Button
              type="button"
              size={compact ? 'icon-sm' : 'sm'}
              variant={isNewRequestView ? 'secondary' : 'ghost'}
              onClick={onNewRequest}
              aria-label={copy.newRequest}
              aria-pressed={isNewRequestView}
              title={compact ? copy.newRequest : undefined}
              className={cn(
                compact
                  ? 'size-11 sm:size-8'
                  : 'min-h-11 justify-center sm:min-h-8'
              )}
            >
              <MessageSquarePlus className="size-4" aria-hidden />
              {!compact ? copy.newRequest : null}
            </Button>
            <Button
              type="button"
              size={compact ? 'icon-sm' : 'sm'}
              variant={isRequestsView ? 'secondary' : 'ghost'}
              onClick={() => {
                if (isRefreshingRequests) return;
                onOpenRequests();
              }}
              aria-label={copy.myRequests}
              aria-pressed={isRequestsView}
              aria-disabled={isRefreshingRequests}
              aria-busy={isRefreshingRequests}
              title={compact ? copy.myRequests : undefined}
              className={cn(
                'aria-disabled:cursor-wait aria-disabled:opacity-60',
                compact
                  ? 'size-11 sm:size-8'
                  : 'min-h-11 justify-center sm:min-h-8'
              )}
            >
              {isRefreshingRequests ? (
                <span aria-hidden="true">
                  <Loader />
                </span>
              ) : (
                <Inbox className="size-4" aria-hidden />
              )}
              {!compact ? copy.myRequests : null}
            </Button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
